import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  hasSystemAccess as utilsHasSystemAccess,
  clearAllStores,
  getInitialTheme,
  initializeLanguage,
  applyTheme,
  updateThemeOnServer,
  updateLanguageOnServer,
  syncLanguage,
  syncUserPreferences,
  ensureCacheOwner,
  performLogoutCleanup,
  createAuthLogout,
} from '@redpanda/utils';
import { checkAuthStatus } from '@redpanda/auth-utils';
import i18n from '../i18n';
import { useColorTheme } from './ColorThemeContext';

const IDENTITY_SERVICE_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000';
const PRODUCTION_DOMAIN = '.redpanda-tech.com';

// Account Portal 不需要 redirect，因為是入口
const authLogout = createAuthLogout(IDENTITY_SERVICE_URL, false);

export type Theme = 'light' | 'dark';
export type Language = 'zh' | 'en' | 'ja';

interface User {
  id: string;
  email: string;
  name: string | null;
  theme: Theme;
  language: Language;
  role: string;
  allowedSystems: string[];
  organization: {
    id: string;
    name: string;
  } | null;
}

interface Organization {
  id: string;
  name: string;
}

interface UserContextType {
  user: User | null;
  cachedUserId: string | null;
  cachedUserRole: string | null;
  organization: Organization | null;
  theme: Theme;
  language: Language;
  updateUserTheme: (newTheme: Theme) => Promise<void>;
  updateUserLanguage: (newLanguage: Language) => Promise<void>;
  logout: (redirectTo?: string) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasSystemAccess: (systemName: string) => boolean;
  refreshUser: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [cachedUserId] = useState<string | null>(() => {
    try { return localStorage.getItem('cachedUserId'); } catch { return null; }
  });
  const [cachedUserRole] = useState<string | null>(() => {
    try { return localStorage.getItem('cachedUserRole'); } catch { return null; }
  });
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [language, setLanguage] = useState<Language>(() => initializeLanguage(i18n.changeLanguage));
  const { colorTheme, applyServerColorTheme } = useColorTheme();
  // 從 localStorage 讀取上次登入狀態作為初始值，避免頁面閃爍
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    () => localStorage.getItem('wasAuthenticated') === 'true'
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // 應用主題變更
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // 兩處（refreshUser / initializeAuth）都會用到的「已通過驗證」寫入邏輯，避免重複
  const applyAuthenticatedUser = useCallback((authUser: User, syncCurrentPrefs: boolean) => {
    setUser(authUser);
    setOrganization(authUser.organization || null);
    setIsAuthenticated(true);
    localStorage.setItem('wasAuthenticated', 'true');
    localStorage.setItem('cachedUserName', authUser.name || '');
    localStorage.setItem('cachedUserId', authUser.id);
    localStorage.setItem('cachedUserRole', authUser.role);
    localStorage.setItem('cachedUserEmail', authUser.email || '');
    localStorage.setItem('cachedUserOrganization', authUser.organization?.name || '');

    syncUserPreferences(authUser, {
      setTheme,
      setLanguage,
      changeLanguage: i18n.changeLanguage,
      setColorTheme: applyServerColorTheme, // 伺服器上的值套回畫面，不再寫回伺服器
      ...(syncCurrentPrefs ? { currentTheme: theme, currentLanguage: language, currentColorTheme: colorTheme } : {}),
    });
  }, [theme, language, colorTheme]);

  // 刷新用戶資料
  const refreshUser = useCallback(async () => {
    try {
      // Account Portal 不需要檢查特定系統權限
      const authResult = await checkAuthStatus(IDENTITY_SERVICE_URL, PRODUCTION_DOMAIN);

      if (authResult.authenticated && authResult.user) {
        applyAuthenticatedUser(authResult.user, true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.setItem('wasAuthenticated', 'false');
        localStorage.removeItem('cachedUserName');
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  }, [applyAuthenticatedUser]);

  // 初始化認證狀態
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);

      try {
        // Account Portal 不需要檢查特定系統權限
        const authResult = await checkAuthStatus(IDENTITY_SERVICE_URL, PRODUCTION_DOMAIN);

        if (authResult.authenticated && authResult.user) {
          // 換帳號：快取不是這個帳號的就清掉並重新載入一次（列表、儀表板的 localStorage 快取不分使用者）
          if (ensureCacheOwner(authResult.user)) {
            window.location.reload();
            return;
          }
          applyAuthenticatedUser(authResult.user, false);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.setItem('wasAuthenticated', 'false');
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setUser(null);
        setIsAuthenticated(false);
        localStorage.setItem('wasAuthenticated', 'false');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // 只在掛載時執行一次；initializeAuth 呼叫的 applyAuthenticatedUser(user, false) 不會用到
    // theme/language/colorTheme，故意不把它列進依賴，避免切換主題/語言時重新觸發整個驗證流程
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserTheme = async (newTheme: Theme) => {
    setTheme(newTheme);
    setUser(prev => prev ? { ...prev, theme: newTheme } : null);
    localStorage.setItem('userTheme', newTheme);

    if (user && isAuthenticated) {
      await updateThemeOnServer(newTheme, IDENTITY_SERVICE_URL);
    }
  };

  const updateUserLanguage = async (newLanguage: Language) => {
    setLanguage(newLanguage);
    setUser(prev => prev ? { ...prev, language: newLanguage } : null);
    syncLanguage(newLanguage, i18n.changeLanguage);

    if (user && isAuthenticated) {
      await updateLanguageOnServer(newLanguage, IDENTITY_SERVICE_URL);
    }
  };

  const hasSystemAccess = (systemName: string): boolean => {
    return utilsHasSystemAccess(systemName, user);
  };

  const logout = useCallback((redirectTo?: string) => {
    localStorage.setItem('wasAuthenticated', 'false');
    localStorage.removeItem('cachedUserName');
    localStorage.removeItem('cachedUserId');
    localStorage.removeItem('cachedUserRole');
    localStorage.removeItem('cachedUserEmail');
    localStorage.removeItem('cachedUserOrganization');
    performLogoutCleanup(() => {
      clearAllStores({
        '../stores/subscriptionStore': ['useSubscriptionStore'],
      });
    });
    if (redirectTo) {
      window.location.href = `${IDENTITY_SERVICE_URL}/auth/logout?redirectUrl=${encodeURIComponent(redirectTo)}`;
    } else {
      authLogout();
    }
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      cachedUserId,
      cachedUserRole,
      organization,
      theme,
      language,
      updateUserTheme,
      updateUserLanguage,
      logout,
      isAuthenticated,
      isLoading,
      hasSystemAccess,
      refreshUser
    }}>
      {children}
    </UserContext.Provider>
  );
};
